import { Button, cn } from '@/core/ui';
import { CaretRight, CheckCircle, Warning, WarningCircle } from '@/core/ui/icons';
import { issueTarget, sortIssues, type IssueTarget } from '../../domain/issues';
import type { FlowIssue } from '../../domain/types';
import { useBuilder } from './context';

interface Props {
  issues: FlowIssue[];
  onFocus: (target: Exclude<IssueTarget, null>) => void;
  onCheck: () => void;
  checking: boolean;
}

/** Os problemas do rascunho: erros impedem publicar, avisos não. Clicar leva ao lugar. */
export function IssuesPanel({ issues, onFocus, onCheck, checking }: Props) {
  const { doc } = useBuilder();
  const errors = issues.filter((i) => i.severity === 'error').length;
  const warnings = issues.length - errors;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-fg-muted">
          {issues.length === 0
            ? 'Nenhum problema.'
            : [
                errors && `${errors} ${errors === 1 ? 'erro' : 'erros'}`,
                warnings && `${warnings} ${warnings === 1 ? 'aviso' : 'avisos'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
        </p>
        <Button size="sm" variant="secondary" loading={checking} onClick={onCheck}>
          Verificar agora
        </Button>
      </div>
      {errors > 0 && (
        <p className="text-xs text-fg-subtle">Erros impedem publicar. Avisos não, mas vale conferir.</p>
      )}

      {issues.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl bg-surface-2 px-4 py-8 text-center">
          <CheckCircle aria-hidden className="size-7 text-brand-500" />
          <p className="mt-2 text-sm text-fg">Tudo certo para publicar.</p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {sortIssues(issues).map((issue, i) => {
            const target = issueTarget(issue, doc);
            const Icon = issue.severity === 'error' ? WarningCircle : Warning;
            const body = (
              <>
                <Icon
                  aria-hidden
                  weight="fill"
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    issue.severity === 'error' ? 'text-rose-500' : 'text-amber-500',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-fg">{issue.message}</span>
                  {target && <span className="block text-xs text-fg-subtle">{where(target, doc)}</span>}
                </span>
                {target && <CaretRight aria-hidden className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" />}
              </>
            );
            return (
              <li key={`${issue.code}-${i}`}>
                {target ? (
                  <button
                    type="button"
                    onClick={() => onFocus(target)}
                    className="flex w-full items-start gap-2.5 rounded-xl bg-surface-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex items-start gap-2.5 rounded-xl bg-surface-2 px-3 py-2.5">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function where(target: Exclude<IssueTarget, null>, doc: ReturnType<typeof useBuilder>['doc']): string {
  const title = (groupId: string | null) => {
    const g = doc.groups.find((x) => x.id === groupId);
    return g ? `“${g.title || 'Card sem título'}”` : 'card';
  };
  if (target.kind === 'edge') return `Ligação saindo de ${title(target.groupId)}`;
  if (target.kind === 'block') {
    const g = doc.groups.find((x) => x.id === target.groupId);
    const n = (g?.blocks.findIndex((b) => b.id === target.blockId) ?? -1) + 1;
    return `Bloco ${n} de ${title(target.groupId)}`;
  }
  return `Card ${title(target.groupId)}`;
}
